import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AdminPlatformSettings, AdminSettingKey } from '@/types/admin-settings';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from '@/hooks/use-toast';

export const useAdminSettings = () => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', ['admin_show_test_profiles', 'admin_visible_roles', 'admin_visible_genders']);

      if (error) throw error;

      // Parse JSONB values to TypeScript
      const settingsMap: Partial<AdminPlatformSettings> = {};
      data?.forEach((setting) => {
        if (setting.key === 'admin_show_test_profiles') {
          settingsMap.admin_show_test_profiles = setting.value as boolean;
        } else if (setting.key === 'admin_visible_roles') {
          // Can be string "profile_based" or array of roles
          const value = setting.value;
          settingsMap.admin_visible_roles = 
            typeof value === 'string' ? value as 'profile_based' : 
            (value as ('sponsor' | 'sponsored')[]);
        } else if (setting.key === 'admin_visible_genders') {
          // Can be string "orientation_based" or array of genders
          const value = setting.value;
          settingsMap.admin_visible_genders = 
            typeof value === 'string' ? value as 'orientation_based' : 
            (value as ('male' | 'female' | 'other')[]);
        }
      });

      debugLogger.debug('[useAdminSettings] Settings loaded:', settingsMap);
      return settingsMap as AdminPlatformSettings;
    },
    staleTime: 60 * 1000, // Cache for 1 minute
  });

  const updateSetting = useMutation({
    mutationFn: async ({ key, value }: { key: AdminSettingKey; value: any }) => {
      debugLogger.debug('[useAdminSettings] Updating setting:', { key, value });

      const { error } = await supabase.rpc('update_admin_setting', {
        p_key: key,
        p_value: value,
      });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      // Invalidate cache to refetch settings
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      // Also invalidate users list to reflect changes
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      
      toast({
        title: 'Configuração atualizada',
        description: 'As mudanças foram aplicadas com sucesso.',
      });

      debugLogger.debug('[useAdminSettings] Setting updated successfully:', variables.key);
    },
    onError: (error) => {
      debugLogger.error('[useAdminSettings] Error updating setting:', error);
      toast({
        title: 'Erro ao atualizar',
        description: 'Não foi possível salvar a configuração.',
        variant: 'destructive',
      });
    },
  });

  return {
    settings,
    isLoading,
    error,
    updateSetting,
  };
};
